// JIT Portal API Service
import { API_BASE_URL, API_ENDPOINTS } from '../utils/constants';

// Get JIT token from localStorage or cookies
const getJITToken = () => {
  return localStorage.getItem('jit_token');
};

// Set JIT token in localStorage
export const setJITToken = (token) => {
  localStorage.setItem('jit_token', token);
};

// Remove JIT token from localStorage
export const removeJITToken = () => {
  localStorage.removeItem('jit_token');
  localStorage.removeItem('jit_user');
};

// Get JIT user from localStorage
export const getJITUser = () => {
  const user = localStorage.getItem('jit_user');
  return user ? JSON.parse(user) : null;
};

// Set JIT user in localStorage
export const setJITUser = (user) => {
  localStorage.setItem('jit_user', JSON.stringify(user));
};

// Parse JWT token to get claims
export const parseJITToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT token:', e);
    return null;
  }
};

// Generic fetch wrapper for JIT API calls
const jitFetch = async (endpoint, options = {}) => {
  const token = getJITToken();

  // Debug logging
  console.log('[JIT API] Endpoint:', endpoint);
  console.log('[JIT API] Token present:', !!token);
  console.log('[JIT API] Token (first 50 chars):', token ? token.substring(0, 50) + '...' : 'none');

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Token expired or invalid - try to get error details
    let errorMsg = 'Authentication required';
    try {
      const errorData = await response.text();
      console.error('[JIT API] 401 Response body:', errorData);
      errorMsg = errorData || 'Authentication required';
    } catch (e) {
      console.error('[JIT API] Could not read error response');
    }
    removeJITToken();
    window.location.href = '/login';
    throw new Error(errorMsg);
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Request failed');
  }

  return data;
};

// JIT API functions

// Get current user info
export const getCurrentUser = () => {
  return jitFetch(API_ENDPOINTS.JIT_ME);
};

// Create a new JIT request
export const createJITRequest = (requestData) => {
  return jitFetch(API_ENDPOINTS.JIT_REQUESTS, {
    method: 'POST',
    body: JSON.stringify(requestData),
  });
};

// Get user's own requests
export const getMyRequests = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.pageSize) queryParams.append('pageSize', params.pageSize);
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  return jitFetch(`${API_ENDPOINTS.JIT_REQUESTS}${queryString}`);
};

// Get a specific request
export const getRequest = (requestId) => {
  return jitFetch(API_ENDPOINTS.JIT_REQUEST_DETAIL.replace('{id}', requestId));
};

// Get active sessions
export const getActiveSessions = () => {
  return jitFetch(API_ENDPOINTS.JIT_SESSIONS_ACTIVE);
};

// Get session history
export const getSessionHistory = () => {
  return jitFetch(API_ENDPOINTS.JIT_SESSIONS_HISTORY);
};

// Revoke a session
export const revokeSession = (sessionId, reason = '') => {
  return jitFetch(API_ENDPOINTS.JIT_SESSION_REVOKE.replace('{id}', sessionId), {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
};

// Get available accounts
export const getAvailableAccounts = () => {
  return jitFetch(API_ENDPOINTS.JIT_ACCOUNTS);
};

// Get available permission sets
export const getAvailablePermissionSets = () => {
  return jitFetch(API_ENDPOINTS.JIT_PERMISSION_SETS);
};

// Approver functions

// Get pending approvals
export const getPendingApprovals = () => {
  return jitFetch(API_ENDPOINTS.JIT_APPROVALS_PENDING);
};

// Get approver request history (all requests for owned accounts, or all for SSO admins)
export const getApproverRequestHistory = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page);
  if (params.pageSize) queryParams.append('pageSize', params.pageSize);
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  return jitFetch(`${API_ENDPOINTS.JIT_APPROVALS_HISTORY}${queryString}`);
};

// Approve a request
export const approveRequest = (requestId, comment = '') => {
  return jitFetch(API_ENDPOINTS.JIT_APPROVE.replace('{id}', requestId), {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
};

// Reject a request
export const rejectRequest = (requestId, comment = '') => {
  return jitFetch(API_ENDPOINTS.JIT_REJECT.replace('{id}', requestId), {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
};

// Multi-level approval functions (for custom permission sets)

// Owner approve a request (step 1 of multi-level approval)
export const ownerApproveRequest = (requestId, comment = '') => {
  return jitFetch(API_ENDPOINTS.JIT_OWNER_APPROVE.replace('{id}', requestId), {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
};

// Owner reject a request
export const ownerRejectRequest = (requestId, comment = '') => {
  return jitFetch(API_ENDPOINTS.JIT_OWNER_REJECT.replace('{id}', requestId), {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
};

// Admin approve a request (final step for custom permission sets)
export const adminApproveRequest = (requestId, comment = '') => {
  return jitFetch(API_ENDPOINTS.JIT_ADMIN_APPROVE.replace('{id}', requestId), {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
};

// Admin reject a request
export const adminRejectRequest = (requestId, comment = '') => {
  return jitFetch(API_ENDPOINTS.JIT_ADMIN_REJECT.replace('{id}', requestId), {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
};

// Get pending admin approvals (SSO admin only)
export const getPendingAdminApprovals = () => {
  return jitFetch(API_ENDPOINTS.JIT_ADMIN_APPROVALS_PENDING);
};

// Get owned accounts
export const getOwnedAccounts = () => {
  return jitFetch(API_ENDPOINTS.JIT_ACCOUNTS_OWNED);
};

// Get all assignments (permanent + JIT) for owned accounts
export const getOwnedAccountAssignments = () => {
  return jitFetch(API_ENDPOINTS.JIT_ACCOUNTS_OWNED_ASSIGNMENTS);
};

// Get active sessions for owned accounts (approver view)
export const getActiveSessionsForOwnedAccounts = () => {
  return jitFetch(API_ENDPOINTS.JIT_APPROVALS_SESSIONS_ACTIVE);
};

// Refresh token
export const refreshToken = () => {
  const token = getJITToken();
  return jitFetch(API_ENDPOINTS.JIT_AUTH_REFRESH, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
};

// AWS Policy helpers for custom permission sets

// Get AWS managed policies for the policy picker
export const getAWSManagedPolicies = (onlyAttachable = true) => {
  const params = onlyAttachable ? '' : '?only_attachable=false';
  return jitFetch(`${API_ENDPOINTS.JIT_AWS_MANAGED_POLICIES}${params}`);
};

// Validate an IAM policy document
export const validateIAMPolicy = (policyDocument, policyType = 'IDENTITY_POLICY') => {
  return jitFetch(API_ENDPOINTS.JIT_AWS_VALIDATE_POLICY, {
    method: 'POST',
    body: JSON.stringify({
      policy_document: policyDocument,
      policy_type: policyType,
    }),
  });
};

export default {
  getJITToken,
  setJITToken,
  removeJITToken,
  getJITUser,
  setJITUser,
  parseJITToken,
  getCurrentUser,
  createJITRequest,
  getMyRequests,
  getRequest,
  getActiveSessions,
  getSessionHistory,
  revokeSession,
  getAvailableAccounts,
  getAvailablePermissionSets,
  getPendingApprovals,
  getApproverRequestHistory,
  approveRequest,
  rejectRequest,
  ownerApproveRequest,
  ownerRejectRequest,
  adminApproveRequest,
  adminRejectRequest,
  getPendingAdminApprovals,
  getOwnedAccounts,
  getActiveSessionsForOwnedAccounts,
  refreshToken,
  getAWSManagedPolicies,
  validateIAMPolicy,
};
